import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { gsap } from 'gsap'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { config } from './config.js'

/**
 * Loaders
 */
const loadingBarElement = document.querySelector('.loading-bar')

let sceneReady = false
const loadingManager = new THREE.LoadingManager(
    // Loaded
    () => {
        window.setTimeout(() => {
            gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0, delay: 1 })
            loadingBarElement.classList.add('ended')
            loadingBarElement.style.transform = ''
        }, 500)

        window.setTimeout(() => {
            sceneReady = true
        }, 2000)
    },
    (itemUrl, itemsLoaded, itemsTotal) => {
        const progressRatio = itemsLoaded / itemsTotal
        loadingBarElement.style.transform = `scaleX(${progressRatio})`
    }
)

// DRACO-Decoder (falls dein Export komprimierte Meshes enthält -> siehe letzte Antworten)
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('/draco/') // Decoder-Dateien selbst hosten, siehe vorheriges Template

const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)
const rgbeLoader = new RGBELoader(loadingManager)

/**
 * Base
 */
const debugObject = {}
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)
const overlayMaterial = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: { uAlpha: { value: 1 } },
    vertexShader: `
        void main() { gl_Position = vec4(position, 1.0); }
    `,
    fragmentShader: `
        uniform float uAlpha;
        void main() { gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha); }
    `
})
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
scene.add(overlay)

/**
 * Update all materials
 */
const updateAllMaterials = () => {
    scene.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return

        const materials = Array.isArray(child.material) ? child.material : [child.material]
        for (const material of materials) {
            if (!material) continue

            if (material.normalMap) {
                // Normaldaten sind keine Farbwerte und dürfen nicht sRGB-kodiert
                // werden, sonst wirken Richtung und Stärke der Map falsch.
                material.normalMap.colorSpace = THREE.NoColorSpace
                const normalMapScale = config.materials?.normalMapScale ?? 1
                material.normalScale.set(normalMapScale, normalMapScale)
                material.normalMap.needsUpdate = true
            }

            if (!(material instanceof THREE.MeshStandardMaterial)) continue

            material.envMapIntensity = debugObject.envMapIntensity

            // Alpha-Masken als harte Cutouts rendern. Dadurch entfallen die
            // Sortierartefakte, die bei transparentem Alpha-Blending entstehen.
            if (material.transparent || material.alphaMap || material.alphaTest > 0) {
                material.transparent = false
                material.alphaTest = 0.5
                material.depthWrite = true
                material.side = THREE.DoubleSide
            }

            material.needsUpdate = true
            child.castShadow = true
            child.receiveShadow = true
        }
    })
}

// Annotationen kommen jetzt vollständig aus der Config
const annotationsConfig = config.annotations

// Laufzeit-Zustand: wird nach dem Laden mit echten Positionen/Elementen befüllt
const points = []
let mixer = null
let rotorAction = null
let towerRotationNode = null

const setActionAnnotationVisibility = (visible) => {
    for (const point of points) {
        if (point.annotation?.id === 'nacelle') {
            point.element.style.display = visible ? '' : 'none'
        }
    }
}

// Einmal gecacht statt bei jedem createPointElement()-Aufruf neu zu suchen
const pointsContainer = document.querySelector('.points-container')

/**
 * Anchors aus dem geladenen Node-Baum extrahieren
 * (Empties mit "ANCHOR_"-Präfix aus Blender, siehe vorherige Antworten)
 */
function extractAnchors(root, prefix = 'ANCHOR_') {
    const anchors = new Map()
    root.traverse((obj) => {
        if (obj.name.startsWith(prefix)) {
            const worldPos = new THREE.Vector3()
            obj.getWorldPosition(worldPos)
            anchors.set(obj.name.replace(prefix, ''), worldPos)
        }
    })
    return anchors
}

/**
 * Baut die DOM-Elemente für einen Annotation-Punkt (Marker + Panel)
 * Ersetzt die vorher hart in HTML kodierten .point-0/.point-1/.point-2
 */
function createPointElement(annotation, position) {
    const el = document.createElement('div')
    const isAnimationPoint = annotation.mode === 'animation'
    const animationHint = annotation.animationLoop
        ? 'Klicken zum Starten / Anhalten'
        : 'Klicken zum Öffnen / Schließen'
    el.className = `point${isAnimationPoint ? ' point--animation' : ''}`
    el.innerHTML = `
        <div class="label">${annotation.icon ?? '+'}</div>
        <div class="text">
            <h3>${annotation.title}</h3>
            <p>${annotation.description}</p>
            ${annotation.animationClip ? `<span class="hint">${isAnimationPoint ? animationHint : 'Klicken zum Abspielen'}</span>` : ''}
        </div>
    `
    pointsContainer.appendChild(el)

    el.querySelector('.label').addEventListener('click', () => {
        flyToAnnotation(position, annotation)

        if (annotation.animationClip) {
            playAnnotationClip(annotation)
        }
    })

    return el
}

const animationActions = new Map()
const animationStates = new Map()
const anchoredLights = []

function addAnchorLights(root) {
    const lightNames = ['Light-01', 'Light-02']

    for (const lightName of lightNames) {
        const anchor = root.getObjectByName(`ANCHOR_${lightName}`)
        if (!anchor) {
            console.warn(`[Lights] Anchor "ANCHOR_${lightName}" nicht gefunden.`)
            continue
        }

        const anchorLightSettings = config.lightEffects?.anchorLights ?? {}
        const lightColor = new THREE.Color(anchorLightSettings.color ?? '#ff3b30')
        const lightIntensity = anchorLightSettings.intensity ?? 20
        const lightDistance = anchorLightSettings.distance ?? 30
        const lightDecay = anchorLightSettings.decay ?? 2

        const light = new THREE.PointLight(lightColor, lightIntensity, lightDistance, lightDecay)
        light.castShadow = false
        light.decay = lightDecay
        light.position.set(0, 0, 0)
        light.distance = lightDistance
        anchor.add(light)
        anchoredLights.push(light)
    }
}

/**
 * Toggelt den zur Annotation gehörigen AnimationClip.
 */
function playAnnotationClip(annotation) {
    if (!mixer) return
    const clip = THREE.AnimationClip.findByName(gltfAnimations, annotation.animationClip)
    if (!clip) {
        console.warn(`[Annotations] Clip "${annotation.animationClip}" nicht gefunden.`)
        return
    }

    let action = animationActions.get(annotation.id)
    if (!action) {
        action = mixer.clipAction(clip)
        action.setLoop(annotation.animationLoop ? THREE.LoopRepeat : THREE.LoopOnce, annotation.animationLoop ? Infinity : 1)
        action.clampWhenFinished = !annotation.animationLoop
        animationActions.set(annotation.id, action)
    }

    if (annotation.animationLoop) {
        if (action.isRunning()) {
            action.stop()
        }
        else {
            if (action.time >= clip.duration) action.reset()
            action.paused = false
            action.play()
        }
    }
    else {
        const isOpen = animationStates.get(annotation.id) ?? false
        const shouldOpen = !isOpen
        animationStates.set(annotation.id, shouldOpen)
        action.paused = false

        if (shouldOpen) {
            if (action.time >= clip.duration) action.time = 0
            action.timeScale = 1
        }
        else {
            if (action.time <= 0) action.time = clip.duration
            action.timeScale = -1
        }
        action.play()

        if (annotation.id === 'nacelle') {
            setActionAnnotationVisibility(!shouldOpen)
        }
    }

    // Rotor-Action separat referenzieren -> wird vom Speed-Slider gesteuert
    if (annotation.id === 'rotor') {
        rotorAction = action
        if (rotorToggleButton) {
            updateRotorButtonState()
        }
    }
}

/**
 * Points of interest — Raycaster für Sichtbarkeit (Occlusion-Check bleibt wie im Original)
 */
const raycaster = new THREE.Raycaster()

/**
 * Lights
 */
const directionalLight = new THREE.DirectionalLight(config.sunlight.color, config.sunlight.intensity)
directionalLight.castShadow = true
directionalLight.shadow.mapSize.set(1024, 1024)
directionalLight.shadow.normalBias = 0.05
directionalLight.position.set(...config.sunlight.position)
const sunlightRotation = (config.sunlight.rotation ?? [0, 0, 0]).map(THREE.MathUtils.degToRad)
directionalLight.rotation.set(...sunlightRotation)
const sunlightDirection = new THREE.Vector3(0, 0, -1).applyEuler(directionalLight.rotation)
directionalLight.target.position.copy(directionalLight.position).add(sunlightDirection)
scene.add(directionalLight)
scene.add(directionalLight.target)



/**
 * Sizes
 */
const sizes = { width: window.innerWidth, height: window.innerHeight }

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 * Start-Werte sind Platzhalter — near/far, Position und Controls-Distanzen
 * werden in frameCameraToModel() automatisch anhand der tatsächlichen
 * Modellgröße gesetzt, sobald das Modell geladen ist. Bis dahin ist die
 * Kamera ohnehin hinter dem Loading-Overlay verborgen.
 */
const camera = new THREE.PerspectiveCamera(60, sizes.width / sizes.height, 0.1, 1000)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
const pinchZoomState = { active: false, distance: 0 }

controls.enableDamping = true
controls.enableRotate = true
controls.enableZoom = true
controls.enablePan = !isTouchDevice
controls.screenSpacePanning = !isTouchDevice
controls.rotateSpeed = 0.9
controls.zoomSpeed = isTouchDevice ? 2.5 : (config.camera?.zoomSpeed ?? 1.8)
controls.dollySpeed = isTouchDevice ? 1.6 : 0.9
controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE
controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY
controls.mouseButtons.RIGHT = isTouchDevice ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN
controls.touches.ONE = THREE.TOUCH.ROTATE
controls.touches.TWO = THREE.TOUCH.DOLLY
controls.touches.THREE = THREE.TOUCH.ROTATE

// Wird in frameCameraToModel() gesetzt, dient flyToAnnotation() als Referenzgröße
// für die Fokus-Distanz (relativ statt fest in Metern, siehe letzte Antwort)
let modelRadius = 1

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
renderer.toneMapping = THREE.ReinhardToneMapping
renderer.toneMappingExposure = 3
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, config.render?.pixelRatioLimit ?? 1.5))

const composer = new EffectComposer(renderer)
composer.addPass(new RenderPass(scene, camera))
const bloomSettings = config.lightEffects?.bloom ?? {}
const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(sizes.width, sizes.height),
    bloomSettings.strength ?? 0.8,
    bloomSettings.radius ?? 0.55,
    bloomSettings.threshold ?? 0.2
)
bloomPass.threshold = bloomSettings.threshold ?? 0.2
bloomPass.strength = bloomSettings.strength ?? 0.8
bloomPass.radius = bloomSettings.radius ?? 0.55
if (bloomSettings.enabled === false) {
    bloomPass.enabled = false
}
composer.addPass(bloomPass)

const performanceSettings = config.render ?? {}
let lastInteractionTime = performance.now()
let isIdleMode = false
let isFullyPaused = false

const idleTimeoutMs = performanceSettings.idleTimeoutMs ?? 60000
const fullyPausedTimeoutMs = performanceSettings.fullyPausedTimeoutMs ?? idleTimeoutMs

const updatePerformanceProfile = () => {
    const now = performance.now()
    const timeSinceInteraction = now - lastInteractionTime
    const shouldBeIdle = timeSinceInteraction >= idleTimeoutMs
    const shouldBeFullyPaused = timeSinceInteraction >= fullyPausedTimeoutMs

    if (shouldBeFullyPaused !== isFullyPaused) {
        isFullyPaused = shouldBeFullyPaused
        isIdleMode = shouldBeIdle

        const targetPixelRatio = isFullyPaused
            ? (performanceSettings.fullyIdlePixelRatio ?? 0.75)
            : (isIdleMode ? (performanceSettings.idlePixelRatioLimit ?? 1.1) : (performanceSettings.pixelRatioLimit ?? 1.5))
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, targetPixelRatio))

        if (bloomPass) {
            bloomPass.strength = isFullyPaused
                ? (performanceSettings.fullyIdleBloomStrength ?? 0)
                : (isIdleMode ? (performanceSettings.idleBloomStrength ?? 0.15) : (bloomSettings.strength ?? 0.8))
        }
    }
    else if (shouldBeIdle !== isIdleMode && !isFullyPaused) {
        isIdleMode = shouldBeIdle
        const targetPixelRatio = isIdleMode
            ? (performanceSettings.idlePixelRatioLimit ?? 1.1)
            : (performanceSettings.pixelRatioLimit ?? 1.5)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, targetPixelRatio))

        if (bloomPass) {
            bloomPass.strength = isIdleMode
                ? (performanceSettings.idleBloomStrength ?? 0.15)
                : (bloomSettings.strength ?? 0.8)
        }
    }
}

const markInteraction = () => {
    lastInteractionTime = performance.now()
    if (isFullyPaused || isIdleMode) {
        isFullyPaused = false
        isIdleMode = false
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, performanceSettings.pixelRatioLimit ?? 1.5))
        if (bloomPass) bloomPass.strength = bloomSettings.strength ?? 0.8
    }
}

window.addEventListener('pointerdown', markInteraction)
window.addEventListener('pointermove', markInteraction)
window.addEventListener('wheel', markInteraction, { passive: true })
window.addEventListener('keydown', markInteraction)

/**
 * HDR environment map
 * PMREM erzeugt die vorgefilterte Version für realistische Materialreflexionen.
 */
const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()
debugObject.envMapIntensity = config.environment.envMapIntensity
scene.background = new THREE.Color(config.environment.backgroundColor)
scene.environmentRotation = new THREE.Euler(0, config.environment.rotationY ?? 0, 0)

rgbeLoader.load(config.environment.hdrPath, (hdrTexture) => {
    hdrTexture.mapping = THREE.EquirectangularReflectionMapping
    scene.environment = pmremGenerator.fromEquirectangular(hdrTexture).texture
    updateAllMaterials()
})

/**
 * Kamera & Schatten-Frustum automatisch an die Modellgröße anpassen
 * ("Modelleisenbahn"-Blickwinkel: erhöht, von schräg oben auf die Mitte).
 * Das ist der Grund, warum Near/Far-Clipping und Kamera-Startposition
 * unabhängig davon funktionieren, ob dein Modell 2m oder 200m groß ist —
 * hier steckt keine feste Annahme über die Modellgröße mehr drin.
 */
function frameCameraToModel(root) {
    const box = new THREE.Box3().setFromObject(root)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())
    const radius = Math.max(size.length() * 0.5, 0.01) // Diagonale/2 als Referenzgröße
    modelRadius = radius

    const cam = config.camera ?? {}
    const elevationFactor = cam.elevationFactor ?? 0.9
    const horizontalFactor = cam.horizontalFactor ?? 0.6
    const nearFactor = cam.nearFactor ?? 0.002
    const farFactor = cam.farFactor ?? 6
    const minDistanceFactor = cam.minDistanceFactor ?? 0.15
    const maxDistanceFactor = cam.maxDistanceFactor ?? 3.5

    // Clipping-Ebenen relativ zur Modellgröße statt fester Meterwerte
    camera.near = radius * nearFactor
    camera.far = radius * farFactor
    camera.updateProjectionMatrix()

    // Modelleisenbahn-Perspektive: erhöht und seitlich versetzt über der Mitte
    orbitStartState.position.set(
        center.x + radius * horizontalFactor,
        center.y + radius * elevationFactor,
        center.z + radius * horizontalFactor
    )
    camera.position.copy(orbitStartState.position)

    orbitStartState.target.copy(center)
    controls.target.copy(orbitStartState.target)
    controls.maxDistance = radius * maxDistanceFactor

    // minDistance darf nicht größer sein als die kleinste konfigurierte
    // Annotation-Fokusdistanz, sonst "blockt" OrbitControls das Heranfahren/
    // Heranzoomen bei kleinen Bauteilen (z.B. Wellen/Getriebe im Inneren),
    // weil es die Kamera bei jedem update() wieder auf minDistance zurückklemmt.
    const configuredFocusDistances = annotationsConfig
        .map((a) => a.focusDistance ?? (a.focusDistanceFactor ?? cam.focusDistanceFactor ?? 0.25) * radius)
    const smallestFocusDistance = Math.min(radius * minDistanceFactor, ...configuredFocusDistances)
    controls.minDistance = smallestFocusDistance * 0.5 // etwas Puffer, damit man noch näher manuell zoomen kann

    controls.update()


}

/**
 * Fährt die Kamera zu einer Annotation heran, ohne die aktuelle Blickrichtung
 * zu verändern — Kamera und Target werden parallel entlang der bestehenden
 * Sichtachse zum Zielpunkt getweent.
 *
 * Fokus-Distanz-Priorität (erster Treffer gewinnt):
 *   1. annotation.focusDistance      – fester Meterwert, für kleine Bauteile
 *   2. annotation.focusDistanceFactor – relativ zur Modellgröße, für einzelne Ausnahmen
 *   3. config.camera.focusDistanceFactor – globaler Default (relativ zur Modellgröße)
 *
 * Grund für die Unterscheidung: Ein globaler, modellgroßen-relativer Faktor
 * funktioniert gut für große Annotationen (z.B. Rotor auf einer 200m-Anlage),
 * zoomt aber bei kleinen Bauteilen (z.B. eine 2m-Welle im Maschinenhaus)
 * viel zu wenig heran, weil er ja an der Gesamtgröße des Modells hängt statt
 * an der Größe des jeweiligen Bauteils.
 */
function flyToAnnotation(position, annotation = {}) {
    let focusDistance
    if (annotation.focusDistance !== undefined) {
        focusDistance = annotation.focusDistance
    }
    else {
        const factor = annotation.focusDistanceFactor ?? config.camera?.focusDistanceFactor ?? 0.25
        focusDistance = modelRadius * factor
    }

    const direction = new THREE.Vector3()
        .subVectors(camera.position, controls.target)
        .normalize()
    const targetCameraPos = new THREE.Vector3()
        .copy(position)
        .addScaledVector(direction, focusDistance)

    controls.enabled = false
    setResetViewVisible(true)

    gsap.to(camera.position, {
        duration: 1.2,
        x: targetCameraPos.x,
        y: targetCameraPos.y,
        z: targetCameraPos.z,
        ease: 'power2.inOut'
    })
    gsap.to(controls.target, {
        duration: 1.2,
        x: position.x,
        y: position.y,
        z: position.z,
        ease: 'power2.inOut',
        onComplete: () => { controls.enabled = true }
    })
}

/**
 * Models
 */
let gltfAnimations = []

gltfLoader.load(
    config.model.url,
    (gltf) => {
        gltf.scene.scale.setScalar(config.model.scale)
        scene.add(gltf.scene)
        towerRotationNode = gltf.scene.getObjectByName(config.model.rotationNode)
        if (!towerRotationNode) {
            console.warn(`[Model] Rotations-Node "${config.model.rotationNode}" nicht gefunden.`)
        }
        updateAllMaterials()
        frameCameraToModel(gltf.scene)

        // EXT_mesh_gpu_instancing wird vom GLTFLoader automatisch erkannt ->
        // deine Vegetation kommt bereits als InstancedMesh-Objekte rein,
        // hier ist nichts weiter zu tun.

        gltfAnimations = gltf.animations
        mixer = new THREE.AnimationMixer(gltf.scene)

        // Anchors einlesen, Lichter an den fest definierten Light-Ankern ansetzen
        // und Punkte gemäß Config aufbauen.
        const anchors = extractAnchors(gltf.scene)
        addAnchorLights(gltf.scene)
        for (const annotation of annotationsConfig) {
            const position = anchors.get(annotation.anchorName)
            if (!position) {
                console.warn(`[Annotations] Kein Anchor "ANCHOR_${annotation.anchorName}" im Modell gefunden.`)
                continue
            }
            points.push({
                position,
                anchor: gltf.scene.getObjectByName(`ANCHOR_${annotation.anchorName}`),
                element: createPointElement(annotation, position),
                annotation
            })
        }
    }
)

/**
 * Rotor-Speed-Slider
 * HTML: <input type="range" id="rotorSpeed" min="0" max="3" step="0.01" value="1" />
 */
const rotorSpeedInput = document.querySelector('#rotorSpeed')
const rotorToggleButton = document.querySelector('#rotorToggle')
const annotationToggleButton = document.querySelector('#annotationToggle')
const uiToggleButton = document.querySelector('#uiToggle')
const resetViewButton = document.querySelector('#resetView')

const orbitStartState = {
    position: new THREE.Vector3(),
    target: new THREE.Vector3()
}

const setResetViewVisible = (visible) => {
    if (!resetViewButton) return
    resetViewButton.hidden = !visible
    resetViewButton.setAttribute('aria-hidden', String(!visible))
}

const resetOrbitView = () => {
    const nacelleAnnotation = annotationsConfig.find((annotation) => annotation.id === 'nacelle')
    if (nacelleAnnotation && (animationStates.get(nacelleAnnotation.id) ?? false)) {
        playAnnotationClip(nacelleAnnotation)
    }

    controls.enabled = true
    setResetViewVisible(false)
    gsap.to(camera.position, {
        duration: 1.1,
        x: orbitStartState.position.x,
        y: orbitStartState.position.y,
        z: orbitStartState.position.z,
        ease: 'power2.inOut'
    })
    gsap.to(controls.target, {
        duration: 1.1,
        x: orbitStartState.target.x,
        y: orbitStartState.target.y,
        z: orbitStartState.target.z,
        ease: 'power2.inOut',
        onUpdate: () => controls.update(),
        onComplete: () => controls.update()
    })
}

const setAnnotationsVisible = (visible) => {
    document.body.classList.toggle('annotations-hidden', !visible)

    if (!annotationToggleButton) return

    annotationToggleButton.textContent = visible ? 'Marker aus' : 'Marker an'
    annotationToggleButton.classList.toggle('is-off', !visible)
    annotationToggleButton.setAttribute('aria-pressed', String(visible))
}

const updateRotorButtonState = () => {
    if (!rotorToggleButton) return

    if (!rotorAction) {
        rotorToggleButton.textContent = 'Einschalten'
        rotorToggleButton.classList.remove('is-on')
        return
    }

    const isRunning = rotorAction.isRunning()
    rotorToggleButton.textContent = isRunning ? 'Ausschalten' : 'Einschalten'
    rotorToggleButton.classList.toggle('is-on', isRunning)
}

if (rotorSpeedInput) {
    rotorSpeedInput.addEventListener('input', (e) => {
        if (rotorAction) rotorAction.timeScale = parseFloat(e.target.value)
    })
}

if (rotorToggleButton) {
    rotorToggleButton.addEventListener('click', () => {
        const rotorAnnotation = annotationsConfig.find((annotation) => annotation.id === 'rotor')
        if (!rotorAnnotation) return

        playAnnotationClip(rotorAnnotation)
        updateRotorButtonState()
    })

    updateRotorButtonState()
}

if (annotationToggleButton) {
    annotationToggleButton.addEventListener('click', () => {
        setAnnotationsVisible(document.body.classList.contains('annotations-hidden'))
    })

    setAnnotationsVisible(true)
}

if (uiToggleButton) {
    uiToggleButton.addEventListener('click', () => {
        const isHidden = document.body.classList.toggle('ui-hidden')
        uiToggleButton.textContent = isHidden ? 'Windradsteuerung' : 'Schließen'
        uiToggleButton.setAttribute('aria-expanded', String(!isHidden))
    })

    uiToggleButton.textContent = document.body.classList.contains('ui-hidden') ? 'Windradsteuerung' : 'Schließen'
    uiToggleButton.setAttribute('aria-expanded', String(!document.body.classList.contains('ui-hidden')))
}

if (resetViewButton) {
    setResetViewVisible(false)
    resetViewButton.addEventListener('click', resetOrbitView)
}

/**
 * Dreht die Gondel bzw. den konfigurierten Turm-Rotations-Parent.
 */
const towerRotationInput = document.querySelector('#towerRotation')
const towerRotationValue = document.querySelector('#towerRotationValue')
if (towerRotationInput) {
    towerRotationInput.addEventListener('input', (e) => {
        const degrees = Number(e.target.value)
        if (towerRotationNode) towerRotationNode.rotation.y = THREE.MathUtils.degToRad(degrees)
        if (towerRotationValue) towerRotationValue.value = `${degrees}°`
        if (towerRotationValue) towerRotationValue.textContent = `${degrees}°`
    })
}

/**
 * Animate
 */
const clock = new THREE.Clock()
let lastFrameTime = 0

const tick = (timestamp) => {
    updatePerformanceProfile()

    const activeFps = isFullyPaused
        ? 1
        : isIdleMode
            ? (performanceSettings.idleMaxFps ?? 6)
            : (performanceSettings.maxFps ?? 60)
    const frameInterval = 1000 / activeFps

    if (timestamp - lastFrameTime < frameInterval) {
        window.requestAnimationFrame(tick)
        return
    }

    lastFrameTime = timestamp
    const delta = clock.getDelta()

    if (!isFullyPaused) {
        controls.update()
        if (mixer) mixer.update(delta)

        if (sceneReady) {
            for (const point of points) {
                if (point.anchor) point.anchor.getWorldPosition(point.position)
                const screenPosition = point.position.clone()
                screenPosition.project(camera)

                raycaster.setFromCamera(screenPosition, camera)
                const intersects = raycaster.intersectObjects(scene.children, true)

                if (intersects.length === 0) {
                    point.element.classList.add('visible')
                }
                else {
                    const intersectionDistance = intersects[0].distance
                    const pointDistance = point.position.distanceTo(camera.position)

                    if (intersectionDistance < pointDistance) {
                        point.element.classList.remove('visible')
                    }
                    else {
                        point.element.classList.add('visible')
                    }
                }

                const translateX = screenPosition.x * sizes.width * 0.5
                const translateY = -screenPosition.y * sizes.height * 0.5
                point.element.style.transform = `translateX(${translateX}px) translateY(${translateY}px)`
            }
        }
    }

    if (!isFullyPaused) {
        composer.render()
    }

    window.requestAnimationFrame(tick)
}

window.requestAnimationFrame(tick)
